import { Logger } from '../common/logger';
import { keyBy, map } from 'lodash';
import { EMPTY_SITE_DATA, findSelectedParkData, getLocation, constructNewPathname } from 'common/state.helper';
import { authXConfig } from '../router/authx-config';
import { privilegesToAppConfig } from './helpers/account.util';
import { checkPermissionsForDashboard, checkUserGroups } from 'common/auth/AuthxAPI';
import {
    PARK_GROUP_NAME,
    PARK_PORTFOLIO_ITEM,
    SCREEN_OPERATIONS,
    SCREEN_REPORTS,
    CCR_OBJECT_ID,
    SCREEN_STATUS,
} from 'common/constants';
import axiosInstance from '../utils/axiosInstance';

const screenListForPark = [SCREEN_OPERATIONS, SCREEN_REPORTS];

export const makeParkListItem = (parkItem) => {
    const parkName = parkItem.datasetName.toUpperCase();
    return {
        key: parkName,
        value: parkName,
        text: parkItem.readableName,
        screens: screenListForPark,
    };
};

export const appData = {
    state: {
        modelError: null,
        isConfigLoading: true,
        configLoadingError: null,
        menuVisible: false,
        parkList: [],
        parkData: null,
        selectedPark: '',
        selectedParkData: EMPTY_SITE_DATA,
        selectedScreen: '',
        isWebSocketEnabled: false,
        regions: [],
    },

    effects: (dispatch) => ({
        checkIfParkChanged(selectedPark, rootState) {
            const currentSelectedPark = rootState.appData.selectedPark;
            if (selectedPark && currentSelectedPark !== selectedPark) {
                Logger.info(`Park changed ${currentSelectedPark} => ${selectedPark}`);

                dispatch.appData.setSelectedPark(selectedPark);

                const selectedParkData = findSelectedParkData();
                dispatch.appData.setSelectedParkData(selectedParkData);
                // Adding this check because on reports screen we don't need anything related to operation screen model,also no websocket connection is required
                if (rootState.appData.isWebSocketEnabled) {
                    dispatch.appData.reconfigureSite();
                    dispatch.appData.resetPark();
                }
            } else {
                Logger.debug('Park not changed');
            }
        },
        checkIfScreenChanged(selectedScreen, rootState) {
            const currentSelectedScreen = rootState.appData.selectedScreen;
            const isOperationScreen = [SCREEN_OPERATIONS, SCREEN_STATUS].includes(selectedScreen);
            if (currentSelectedScreen !== selectedScreen) {
                Logger.info(`Screen changed ${currentSelectedScreen} => ${selectedScreen}`);
                dispatch.appData.setIsWebSocketEnabled(isOperationScreen);
                dispatch.appData.setSelectedScreen(selectedScreen);
                // Adding "isOperationScreen" check because on reports screen we don't need anything related to operation screen model, also no websocket connection is required
                if (screenListForPark.includes(currentSelectedScreen) && isOperationScreen) {
                    Logger.info(`Screen changed to ${currentSelectedScreen}. Reconfiguring site and resetting park.`);
                    dispatch.appData.reconfigureSite();
                    dispatch.appData.resetPark();
                }
            } else {
                Logger.debug('Screen not changed');
            }
        },
        async fetchAppConfig(_, rootState) {
            try {
                Logger.log('Loading app data');
                const { token, user } = rootState.account;
                const parksData = await axiosInstance.get(authXConfig.API.ConfigAPI.parkURL);
                const parkObjects = parksData.data.map((park) => {
                    const { siteDefinition, ...restOfPark } = park;
                    return { ...restOfPark, ...siteDefinition };
                });
                const hasCcrPrivilege = user?.centralControlRoomView || false;
                const isAdmin = user?.pbAdmin || false;

                const { allowedScreens, allowedRoutes } = await privilegesToAppConfig(hasCcrPrivilege, {
                    parkObjects,
                    ccrObjectId: CCR_OBJECT_ID,
                });

                dispatch.account.setIsAdmin(isAdmin);
                dispatch.account.setPrivilegesConfig({ allowedScreens, allowedRoutes });

                const parkData = keyBy(parkObjects, (o) => o.datasetName);
                dispatch.appData.setParkData(parkData);
                const selectedParkData = findSelectedParkData();
                dispatch.appData.setSelectedParkData(selectedParkData);

                dispatch.appData.filterParkListByAuth();
                dispatch.appData.reconfigureSite();
                dispatch.appData.setIsConfigLoading(false);
            } catch (err) {
                Logger.error('Error loading app config', err);
                dispatch.appData.setConfigLoadingError('Error loading app configuration!');
            }
        },
        async reconfigureSite(_, rootState) {
            const { webSocket } = rootState;
            const { isWebSocketEnabled, selectedPark } = rootState.appData;
            const isConnecting = webSocket.connecting;

            Logger.info(`[${selectedPark}] Reconfigure site`);

            if (!isConnecting) {
                dispatch.webSocket.disconnect();
            }
            if (selectedPark && isWebSocketEnabled && !isConnecting) {
                dispatch.webSocket.connect();
            }
        },

        async selectScreen({ value }, rootState) {
            Logger.info(`Changing screen to ${value}`);
            const { selectedPark } = rootState.appData;
            const { pathname } = getLocation(rootState);

            dispatch({
                type: 'history/push',
                pathname: constructNewPathname(selectedPark, value, pathname),
            });
        },
        async filterParkListByAuth(_, rootState) {
            const allowedScreens = rootState.account.allowedScreens;
            const allowedParks = allowedScreens && Object.keys(allowedScreens).map((p) => p.toUpperCase());
            const { parkData } = rootState.appData;
            const parkList = map(parkData, makeParkListItem);
            const resultParkList = allowedScreens
                ? parkList
                      .filter((p) => allowedParks.includes(p.value))
                      .map((p) => ({
                          ...p,
                          screens: p.screens.filter((s) => {
                              const availableScreens = allowedScreens[p.value.toLowerCase()].map((screen) =>
                                  screen.toUpperCase()
                              );
                              return availableScreens.includes(s);
                          }),
                          group: PARK_GROUP_NAME,
                      }))
                : [];
            dispatch.appData.setFilteredParkList([PARK_PORTFOLIO_ITEM, ...resultParkList]);
        },
        async resetPark() {
            Logger.info('Reset Park');
            dispatch.plantData.reset();
            dispatch.weather.reset();
            dispatch.turbines.reset();
            dispatch.logTabs.reset();
            dispatch.turbines.deselectTurbine();
            dispatch.offshorePlatforms.deselectPlatform();
            dispatch.powerGrids.reset();
            dispatch.filter.reset();
            dispatch.offshorePlatforms.reset();
            dispatch.systemStatus.reset();
        },
    }),
    reducers: {
        setModelError(state, modelError) {
            return { ...state, modelError };
        },
        setIsConfigLoading(state, isConfigLoading) {
            return { ...state, isConfigLoading };
        },
        setConfigLoadingError(state, configLoadingError) {
            return { ...state, configLoadingError };
        },
        setSelectedPark(state, selectedPark) {
            Logger.info(`Selected Park: ${selectedPark.toUpperCase()}`);
            return { ...state, selectedPark: selectedPark.toUpperCase() };
        },
        setSelectedParkData(state, selectedParkData) {
            Logger.info('Selected Parkdata', selectedParkData);
            return { ...state, selectedParkData };
        },
        setParkData(state, parkData) {
            Logger.info('Set park data:', parkData);
            return { ...state, parkData };
        },
        setRegions(state, regions) {
            Logger.info('Set regions:', regions);
            return { ...state, regions };
        },
        setSelectedScreen(state, selectedScreen) {
            Logger.info(`Selected Screen: ${selectedScreen.toUpperCase()}`);
            return { ...state, selectedScreen: selectedScreen.toUpperCase() };
        },
        setIsWebSocketEnabled(state, isWebSocketEnabled) {
            return { ...state, isWebSocketEnabled };
        },
        toggleMenu(state) {
            Logger.info('Toggle menu visibility');
            return { ...state, menuVisible: !state.menuVisible };
        },
        setFilteredParkList(state, parkList) {
            Logger.info('Setting parkList to:', parkList);
            return { ...state, parkList: [...parkList] };
        },
    },
};
